import { Navigate } from "react-router";
import { Card } from "../planning/components/Card";
import { useTranslation } from "react-i18next";
import { IconExternalLink } from "@tabler/icons-react";
import { LoadingState } from "molecules/loadingState/LoadingState";
import { ButtonLink } from "../../common/components/atoms/button/ButtonLink.js";
import { useGetLegalEntitiesQuery } from "src/common/service/nexus/nexusApi.js";
import {
	ALL_COMPANY_DOMAINS,
	ALL_LEGAL_ENTITIES,
} from "src/common/utils/lastActiveContext.js";
import { useLastActiveContext } from "src/common/utils/hooks/useLastActiveContext.js";
import { useLoadedCompanyDomains } from "src/routes/routeDataHooks.js";
import { useUser } from "src/common/context/userContextUtils.js";
import { AllDomainsDashboard } from "../startDashboard/AllDomainsDashboard.js";

/** Lands on the remembered company, else the domain's only company, else the domain-level Start. */
const HomeRedirect = ({ companyDomainId }: { companyDomainId: string }) => {
	// WHY currentData: during a domain switch `data` still holds the previous domain's companies.
	const {
		currentData: legalEntities,
		isLoading,
		isFetching,
	} = useGetLegalEntitiesQuery(companyDomainId);

	// Reactive hook, not readLastActiveContext(): lets a still-pending remote reconciliation land before this redirect fires.
	const { value: lastActiveContext } = useLastActiveContext();

	if (isLoading || isFetching) {
		return <LoadingState />;
	}

	const rememberedLegalEntity = legalEntities?.find(
		(legalEntity) => legalEntity.id === lastActiveContext?.legalEntityId,
	);
	const onlyLegalEntity =
		legalEntities?.length === 1 ? legalEntities[0] : undefined;
	const landingLegalEntity = rememberedLegalEntity ?? onlyLegalEntity;

	if (landingLegalEntity) {
		return (
			<Navigate
				to={`/company-domains/${companyDomainId}/legal-entities/${landingLegalEntity.id}`}
			/>
		);
	}

	return <Navigate to={`/company-domains/${companyDomainId}`} />;
};

export const Home = () => {
	const { t } = useTranslation();
	const companyDomains = useLoadedCompanyDomains();
	const user = useUser();
	const { value: lastActiveContext } = useLastActiveContext();

	if (companyDomains.length === 0) {
		return (
			<Card className="mx-auto mt-10 max-w-[400px] space-y-4 p-4">
				<div className="text-base">
					{t(
						"Your account is not connected to any company, please contact support to get started",
					)}
				</div>
				<ButtonLink
					reloadDocument
					to="https://www.exopen.se/kontakta-oss"
					target="_blank"
					icon={<IconExternalLink />}
					iconPosition="right"
				>
					{t("Contact support")}
				</ButtonLink>
			</Card>
		);
	}

	if (
		lastActiveContext?.companyDomainId === ALL_COMPANY_DOMAINS &&
		user.userIsSuperAdmin()
	) {
		return <AllDomainsDashboard />;
	}

	const rememberedCompanyDomain = companyDomains.find(
		(companyDomain) =>
			`${companyDomain.id}` === lastActiveContext?.companyDomainId,
	);
	const targetCompanyDomainId = rememberedCompanyDomain
		? `${rememberedCompanyDomain.id}`
		: `${companyDomains[0].id}`;

	const preferAllCompanies =
		Boolean(rememberedCompanyDomain) &&
		lastActiveContext?.legalEntityId === ALL_LEGAL_ENTITIES;

	if (preferAllCompanies) {
		return <Navigate to={`/company-domains/${targetCompanyDomainId}`} />;
	}

	return <HomeRedirect companyDomainId={targetCompanyDomainId} />;
};
